import styled, { css } from 'styled-components';
import { mq, containerWidth } from '~/styles/grid';
import { getThemeValueByName } from '~/helpers/getColors';
import CTA from '../../atoms/CTA';
import { Copytext } from '../../atoms/Copytext';
import { CopyStandardSkin } from '../../atoms/Copytext/CopytextSkins';
import { Headline500 } from '../../atoms/Headline';
import Headline from '../../atoms/Headline/Headline';
import { Headline800Skin } from '../../atoms/Headline/HeadlineSkins';

export const HeadlineStyled = styled.p`
  line-height: 66%;
  height: 0.7em;
  margin-left: -0.05em;
  color: white;
  font-family: ${(props) => props.theme.font.black};
  word-break: keep-all;
  font-size: ${(props) => props.fontSize};

  letter-spacing: ${(props) =>
    props.theme.highlightHero.headline.letterSpacing.xl};

  @media (${mq.xxs}) {
    margin-left: -0.9vw;
    letter-spacing: ${(props) =>
      props.theme.highlightHero.headline.letterSpacing.xxs};
  }

  @media (${mq.xl}) {
    font-size: ${(props) => props.theme.highlightHero.headline.fontSize.xl};
    letter-spacing: ${(props) =>
      props.theme.highlightHero.headline.letterSpacing.xl};
  }
`;

export const SubHeadlineStyled = styled.p`
  color: white;
  font-family: ${(props) => props.theme.font.regular};
  line-height: 120%;
  font-size: ${(props) =>
    props.theme?.highlightHero?.subHeadline?.fontSize?.xs || 'inherit'};
  margin-top: 0px;
  @media (${mq.lgUp}) {
    font-size: ${(props) =>
      props.theme?.highlightHero?.subHeadline?.fontSize?.xl || 'inherit'};
  }
`;

export const Parallelogram = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.theme.color.basicHeadlineColor};
  position: absolute;
  transform: skewX(0deg) skewY(-4deg) translateY(-4vw);
  left: 0;
  z-index: 10;

  @media (${mq.lgUp}) {
    width: ${({ teaserFullWidth }) => (teaserFullWidth ? '70%' : '52.5%')};
    transform: skewX(-22deg) skewY(0deg);
    top: 0;
  }
`;

export const UnskewHelper = styled(Parallelogram)`
  @media (${mq.lgUp}) {
    transform: skewX(22deg);
  }
`;

export const Lines = styled.div`
  font-family: ${(props) => props.theme.font.bold};
  font-size: ${(props) => props.theme.highlightHero.headline.fontSize.xs};
  display: flex;
  flex-direction: column;

  @media (${mq.smUp}) and (${mq.mdDown}) {
    max-width: calc(100% - 100px);
    margin: 0 auto;
  }

  @media (${mq.smUp}) {
    font-size: ${(props) => props.theme.highlightHero.headline.fontSize.md};
    line-height: ${(props) => props.theme.highlightHero.headline.lineHeight.md};
  }

  @media (${mq.xl}) {
    font-size: ${(props) => props.theme.highlightHero.headline.fontSize.xl};
  }
`;

export const HighlightedText = styled.span`
  margin: 0;
  color: ${(props) =>
    props.highlight ? props.theme.color.highlighted : 'white'};
`;

export const HeroContainer = styled.div`
  width: 100%;
  max-width: ${(props) =>
    props.$fullWidth ? '100%' : props.theme.hero.bgImageMaxWidth.xxl};

  position: relative;
  display: flex;
  justify-content: center;
  transition: margin 0.5s ease-out;
  overflow: hidden;

  @media (${mq.sm}) {
    min-height: ${(props) =>
      props.$teaserFullWidth
        ? props.theme.teaserFullWidth.height.sm
        : 'calc((100%) * 2 / 3)'};
    max-width: ${(props) =>
      props.$fullWidth ? '100%' : props.theme.hero.bgImageMaxWidth.sm};
  }

  @media (${mq.xs}) {
    max-width: ${(props) =>
      props.$fullWidth ? '100%' : props.theme.hero.bgImageMaxWidth.xs};
  }

  ${(props) =>
    props.$orientation === 'RIGHT_NARROW' ||
    props.$orientation === 'LEFT_NARROW'
      ? css`
          min-height: ${props.theme.hero.bgImageHeightNarrowWide.default};
          @media (${mq.lgDown}) {
            min-height: ${props.theme.hero.bgImageHeightNarrowWide.lg};
          }
          @media (${mq.md}) {
            min-height: ${props.theme.hero.bgImageHeightNarrowWide.md};
          }
        `
      : ''}
`;

export const HeroHeadlineStyled = styled(Headline)`
  ${Headline800Skin};
  text-transform: uppercase;

  @media (${mq.mdDown}) {
    font-size: ${(props) => props.theme.headline.h800.xs.fontSize};
    line-height: ${(props) => props.theme.headline.h800.xs.lineHeight};
  }

  @media (${mq.xs}) {
    font-size: ${(props) => props.theme.headline.h700.xs.fontSize};
    line-height: ${(props) => props.theme.headline.h700.xs.lineHeight};
  }

  strong {
    font-family: ${(props) => props.theme.font.bold};
  }
`;

export const HeroSubHeadlineStyled = styled(Headline500)`
  margin-top: ${(props) => props.theme.spacing.xl.spacing400};
  @media (${mq.smDown}) {
    margin-top: ${(props) => props.theme.spacing.xs.spacing400};
  }
`;

export const HeroCopyStyled = styled(Copytext)`
  ${CopyStandardSkin};
  display: block;
  margin-top: ${(props) => props.theme.spacing.xl.spacing500};
  @media (${mq.smDown}) {
    margin-top: ${(props) => props.theme.spacing.xs.spacing500};
  }
`;

export const HeroCTAStyled = styled(CTA)`
  pointer-events: all;
  margin-top: ${(props) => props.theme.spacing.xl.spacing500};
  @media (${mq.smDown}) {
    margin-top: ${(props) => props.theme.spacing.xs.spacing500};
  }
`;

export const HeroSection = styled.section`
  position: relative;

  ${(props) =>
    props.$fullWidth &&
    css`
      width: 100%;
    `};
`;

export const HeroInner = styled.div`
  max-width: ${(props) =>
    props.$fullWidth ? '100%' : props.theme.hero.bgImageMaxWidth.xxl};
  margin: 0 auto;
`;

export const HeroMedia = styled.div`
  width: 100%;
  /* aspect-ratio: 16 / 7; */
  overflow: hidden;
  display: flex;
  justify-content: center;

  @media (${mq.xs}) {
    aspect-ratio: unset;
  }
`;

export const HeroContentSlot = styled.div`
  width: 100%;
  pointer-events: none;

  display: flex;
  justify-content: center;
  position: relative;
  z-index: 2;
  margin-top: clamp(-90px, -14vw, -100px);
  @media (${mq.xs}) {
    margin-top: clamp(-80px, -15vw, -60px);
  }
  ${(props) =>
    props.$fullWidth &&
    css`
      margin-top: clamp(-140px, -10vw, -60px);
    `};
`;

export const HeroContent = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: clamp(20px, 3vw, 35px);

  background: ${(props) => props.theme.color.white500};

  ${(props) =>
    props.$styling === 'GRAY' &&
    css`
      background: ${props.theme.color.gray50};
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p,
      span {
        color: ${props.theme.color.primary800};
      }
    `}

  ${(props) =>
    props.$styling === 'BRAND' &&
    css`
      background: ${getThemeValueByName(
        props.theme,
        props.theme.hero.background,
      )};
      color: ${props.theme.color.white500};

      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p,
      span:not(a span) {
        color: ${props.theme.color.white500};
      }
    `}

      @media (${mq.xs}) {
    margin: 0 8px;
  }
  @media (${mq.sm}) {
    max-width: calc(100% - 150px);
  }
  @media (${mq.md}) {
    max-width: calc(${containerWidth.md}px - 50px);
  }
  @media (${mq.lg}) {
    max-width: calc(${containerWidth.lg}px - 50px);
  }
  @media (${mq.xl}) {
    max-width: ${containerWidth.xl}px;
  }
  @media (${mq.xxl}) {
    max-width: ${containerWidth.xl}px;
  }
`;
